import { useMemo } from 'react'
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Chip,
  Divider,
  Tooltip
} from '@mui/material'
import InfoIcon from '@mui/icons-material/Info'

// Calculate percentile
function percentile(arr, p) {
  if (arr.length === 0) return 0
  const sorted = [...arr].sort((a, b) => a - b)
  const index = (p / 100) * (sorted.length - 1)
  const lower = Math.floor(index)
  const upper = Math.ceil(index)
  if (lower === upper) return sorted[lower]
  return sorted[lower] * (upper - index) + sorted[upper] * (index - lower)
}

// Calculate standard deviation
function stdDev(arr) {
  if (arr.length === 0) return 0
  const mean = arr.reduce((a, b) => a + b, 0) / arr.length
  const squaredDiffs = arr.map(v => Math.pow(v - mean, 2))
  return Math.sqrt(squaredDiffs.reduce((a, b) => a + b, 0) / arr.length)
}

// Calculate confidence interval (95%)
function confidenceInterval(arr) {
  if (arr.length < 2) return { lower: 0, upper: 0 }
  const mean = arr.reduce((a, b) => a + b, 0) / arr.length
  const sd = stdDev(arr)
  const se = sd / Math.sqrt(arr.length)
  const z = 1.96 // 95% CI
  return {
    lower: mean - z * se,
    upper: mean + z * se
  }
}

function StatisticsPanel({ data, title = 'Statistical Analysis' }) {
  const stats = useMemo(() => {
    const efficiencies = data.filter(d => d.efficiency_pct != null).map(d => d.efficiency_pct)
    const kwTonBefore = data.filter(d => d.kw_per_ton_before != null).map(d => d.kw_per_ton_before)
    const kwTonAfter = data.filter(d => d.kw_per_ton_after != null).map(d => d.kw_per_ton_after)
    const ages = data.filter(d => d.equipment_age != null).map(d => d.equipment_age)
    const capacities = data.filter(d => d.capacity_tons != null).map(d => parseFloat(d.capacity_tons) || 0)
    const savings = data.filter(d => d.annual_savings != null).map(d => d.annual_savings)

    const effCI = confidenceInterval(efficiencies)
    const kwCI = confidenceInterval(kwTonBefore.map((b, i) => kwTonAfter[i] ? ((b - kwTonAfter[i]) / b) * 100 : 0).filter(v => v > 0))

    return {
      sampleSize: data.length,
      efficiency: {
        mean: efficiencies.length > 0 ? (efficiencies.reduce((a, b) => a + b, 0) / efficiencies.length).toFixed(2) : 'N/A',
        median: efficiencies.length > 0 ? percentile(efficiencies, 50).toFixed(2) : 'N/A',
        stdDev: efficiencies.length > 0 ? stdDev(efficiencies).toFixed(2) : 'N/A',
        min: efficiencies.length > 0 ? Math.min(...efficiencies).toFixed(1) : 'N/A',
        max: efficiencies.length > 0 ? Math.max(...efficiencies).toFixed(1) : 'N/A',
        p25: efficiencies.length > 0 ? percentile(efficiencies, 25).toFixed(2) : 'N/A',
        p75: efficiencies.length > 0 ? percentile(efficiencies, 75).toFixed(2) : 'N/A',
        ci95Lower: effCI.lower.toFixed(2),
        ci95Upper: effCI.upper.toFixed(2),
        n: efficiencies.length
      },
      kwTon: {
        meanBefore: kwTonBefore.length > 0 ? (kwTonBefore.reduce((a, b) => a + b, 0) / kwTonBefore.length).toFixed(3) : 'N/A',
        meanAfter: kwTonAfter.length > 0 ? (kwTonAfter.reduce((a, b) => a + b, 0) / kwTonAfter.length).toFixed(3) : 'N/A',
        reduction: kwTonBefore.length > 0 && kwTonAfter.length > 0
          ? (((kwTonBefore.reduce((a, b) => a + b, 0) / kwTonBefore.length) - (kwTonAfter.reduce((a, b) => a + b, 0) / kwTonAfter.length)) / (kwTonBefore.reduce((a, b) => a + b, 0) / kwTonBefore.length) * 100).toFixed(1)
          : 'N/A',
        ci95Lower: kwCI.lower.toFixed(2),
        ci95Upper: kwCI.upper.toFixed(2),
        n: Math.min(kwTonBefore.length, kwTonAfter.length)
      },
      equipment: {
        meanAge: ages.length > 0 ? (ages.reduce((a, b) => a + b, 0) / ages.length).toFixed(1) : 'N/A',
        meanCapacity: capacities.length > 0 ? (capacities.reduce((a, b) => a + b, 0) / capacities.length).toFixed(0) : 'N/A',
        totalCapacity: capacities.length > 0 ? Math.round(capacities.reduce((a, b) => a + b, 0)).toLocaleString() : 'N/A'
      },
      savings: {
        total: savings.length > 0 ? savings.reduce((a, b) => a + b, 0) : 0,
        mean: savings.length > 0 ? (savings.reduce((a, b) => a + b, 0) / savings.length).toFixed(0) : 'N/A',
        median: savings.length > 0 ? percentile(savings, 50).toFixed(0) : 'N/A',
        n: savings.length
      }
    }
  }, [data])

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {title}
          </Typography>
          <Chip label={`n=${stats.sampleSize}`} size="small" color="primary" />
          <Tooltip title="Statistical summary for researchers and engineers. 95% CI = 95% Confidence Interval.">
            <InfoIcon sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help' }} />
          </Tooltip>
        </Box>

        <Grid container spacing={2}>
          {/* Efficiency Statistics */}
          <Grid item xs={12} md={6}>
            <Typography variant="caption" color="primary" fontWeight={600}>
              EFFICIENCY IMPROVEMENT (%)
            </Typography>
            <Table size="small" sx={{ mt: 0.5 }}>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ py: 0.5, border: 0 }}>Mean (n={stats.efficiency.n})</TableCell>
                  <TableCell align="right" sx={{ py: 0.5, border: 0, fontWeight: 600 }}>{stats.efficiency.mean}%</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ py: 0.5, border: 0 }}>Median</TableCell>
                  <TableCell align="right" sx={{ py: 0.5, border: 0 }}>{stats.efficiency.median}%</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ py: 0.5, border: 0 }}>Std Dev (σ)</TableCell>
                  <TableCell align="right" sx={{ py: 0.5, border: 0 }}>{stats.efficiency.stdDev}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ py: 0.5, border: 0 }}>95% CI</TableCell>
                  <TableCell align="right" sx={{ py: 0.5, border: 0 }}>[{stats.efficiency.ci95Lower}, {stats.efficiency.ci95Upper}]</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ py: 0.5, border: 0 }}>IQR (P25-P75)</TableCell>
                  <TableCell align="right" sx={{ py: 0.5, border: 0 }}>{stats.efficiency.p25} - {stats.efficiency.p75}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ py: 0.5, border: 0 }}>Range (Min-Max)</TableCell>
                  <TableCell align="right" sx={{ py: 0.5, border: 0 }}>{stats.efficiency.min} - {stats.efficiency.max}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Grid>

          {/* kW/Ton Statistics */}
          <Grid item xs={12} md={6}>
            <Typography variant="caption" color="secondary" fontWeight={600}>
              kW/TON PERFORMANCE (n={stats.kwTon.n})
            </Typography>
            <Table size="small" sx={{ mt: 0.5 }}>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ py: 0.5, border: 0 }}>Mean Before</TableCell>
                  <TableCell align="right" sx={{ py: 0.5, border: 0 }}>{stats.kwTon.meanBefore} kW/Ton</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ py: 0.5, border: 0 }}>Mean After</TableCell>
                  <TableCell align="right" sx={{ py: 0.5, border: 0, fontWeight: 600, color: 'success.main' }}>{stats.kwTon.meanAfter} kW/Ton</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ py: 0.5, border: 0 }}>Mean Reduction</TableCell>
                  <TableCell align="right" sx={{ py: 0.5, border: 0, fontWeight: 600 }}>{stats.kwTon.reduction}%</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ py: 0.5, border: 0 }}>95% CI Reduction</TableCell>
                  <TableCell align="right" sx={{ py: 0.5, border: 0 }}>[{stats.kwTon.ci95Lower}, {stats.kwTon.ci95Upper}]%</TableCell>
                </TableRow>
              </TableBody>
            </Table>

            <Divider sx={{ my: 1 }} />

            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              EQUIPMENT PORTFOLIO
            </Typography>
            <Table size="small" sx={{ mt: 0.5 }}>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ py: 0.5, border: 0 }}>Mean Age</TableCell>
                  <TableCell align="right" sx={{ py: 0.5, border: 0 }}>{stats.equipment.meanAge} years</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ py: 0.5, border: 0 }}>Mean Capacity</TableCell>
                  <TableCell align="right" sx={{ py: 0.5, border: 0 }}>{stats.equipment.meanCapacity} tons</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ py: 0.5, border: 0 }}>Total Capacity</TableCell>
                  <TableCell align="right" sx={{ py: 0.5, border: 0 }}>{stats.equipment.totalCapacity} tons</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Grid>
        </Grid>

        {/* Savings Summary */}
        <Box sx={{ mt: 2, p: 1.5, bgcolor: 'rgba(76, 175, 80, 0.12)', borderRadius: 1, border: '1px solid', borderColor: 'success.main' }}>
          <Typography variant="caption" fontWeight={600} color="success.main">
            DOCUMENTED SAVINGS (n={stats.savings.n})
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
            <Typography variant="body2" color="success.main">
              Total: <strong>${stats.savings.total.toLocaleString()}</strong>
            </Typography>
            <Typography variant="body2" color="success.main">
              Mean: <strong>${stats.savings.mean}</strong>
            </Typography>
            <Typography variant="body2" color="success.main">
              Median: <strong>${stats.savings.median}</strong>
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  )
}

export default StatisticsPanel
